import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { Alert, Button, Grid2, Paper, Stack, Typography } from '@mui/material';

import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { usePageTitle } from '@enroller/utils';
import { yesNoOptions } from '../../../constants/yesNoOptions.ts';
import { defaultValues, HouseHomeFormValues, houseHomeSchema, petDefaultValues } from './HouseAndHomeFormValues.ts';
import { zodResolver } from '@hookform/resolvers/zod';

const MAX_CHARACTERS_DESCRIPTION = 1000;

const typeOfHouseOptions = [{ value: 'House' }, { value: 'Apartment' }, { value: 'Condo' }, { value: 'Townhouse' }, { value: 'Other' }];

const areaYouLiveInOptions = [{ value: 'Urban' }, { value: 'Suburban' }, { value: 'Rural' }];

const functioningBusinessResidenceTypeOptions = [
  { label: 'Daycare', value: 'Daycare' },
  { label: 'Farm', value: 'Farm' },
  { label: 'Business Office', value: 'Business Office' },
  {
    label: 'Other',
    value: 'Other',
    textField: { label: '', name: 'housingType.isBusiness.other', placeholder: 'Please elaborate' },
  },
];

const typeOfAnimalOptions = [
  { label: 'Dog', value: 'Dog' },
  { label: 'Cat', value: 'Cat' },
  { label: 'Bird', value: 'Bird' },
  { label: 'Fish', value: 'Fish' },
  { label: 'Other', value: 'Other' },
];

const relativeSizeOfAnimalOptions = [
  { label: 'Small', value: 'Small' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Large', value: 'Large' },
];

const isYourHouseholdOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Moderately Active', value: 'ModeratelyActive' },
  { label: 'Quiet', value: 'Quiet' },
];

const amenitiesAccessOptions = [
  { label: 'Computer', value: 'Computer' },
  { label: 'Internet', value: 'Internet' },
  { label: 'Laundry Machine', value: 'LaundryMachine' },
  { label: 'Piano', value: 'Piano' },
  { label: 'Swimming Pool', value: 'SwimmingPool' },
  { label: 'Television', value: 'Television' },
  { label: 'Video Game Consoles', value: 'VideoGameConsoles' },
  { label: 'Home Phone', value: 'HomePhone' },
];

const utilitiesAccessOptions = [
  { label: 'Air Conditioning', value: 'AirConditioning' },
  { label: 'Electricity', value: 'Electricity' },
  { label: 'Heat', value: 'Heat' },
  { label: 'Running Water', value: 'RunningWater' },
  {
    label: 'Other',
    value: 'Other',
    textField: { label: '', name: 'activityLevel.utilitiesAccess.other', placeholder: 'Please explain' },
  },
];

const languageSpokenAtHomeOptions = [
  { label: 'English', value: 'English' },
  {
    label: 'Other',
    value: 'Other',
    textField: { label: '', name: 'activityLevel.languageSpokenAtHome.other', placeholder: 'Please explain' },
  },
];

const ownBedroomOptions = [
  { label: 'Yes', value: 'Yes' },
  {
    label: 'No',
    value: 'No',
    textField: {
      label: '',
      placeholder: 'Who will they share a bedroom with?',
      name: 'activityLevel.ownBedroom.explanation',
      helperText: '(Students must have their own bed. The host sibling must be the same gender as the student and similar age.)',
    },
  },
];

const discoverySourceOptions = [
  { label: 'Google', value: 'Google' },
  { label: 'Craigslist', value: 'Craigslist' },
  { label: 'Facebook', value: 'Facebook' },
  { label: 'ZipRecruiter', value: 'ZipRecruiter' },
  { label: 'VolunteerMatch.com', value: 'VolunteerMatch.com' },
  { label: 'Church/School', value: 'Church/School' },
  { label: 'Family/Friend', value: 'Family/Friend' },
  { label: 'Community Event', value: 'Community Event' },
  {
    label: 'Other',
    value: 'Other',
    textField: { label: '', placeholder: 'Please specify', name: 'reasons.discoverySource.other' },
  },
  { label: 'Greenheart Network (Host family, student or Local Coordinator)', value: 'GreenheartNetwork' },
];

export const HouseHomeForm = () => {
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  useEffect(() => {
    setHeaderTitle({ title: 'House & Home', subtitle: '' });
    setPageTitle('House & Home');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? houseHomeSchema.required : houseHomeSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const { control, handleSubmit, watch } = useForm<HouseHomeFormValues>({ defaultValues, resolver });

  const { fields: pets, append: addPet } = useFieldArray({
    control,
    name: 'petsOwned.pets',
  });

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        console.log('Form submitted:', data);
      })(),
    [handleSubmit],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  const [isBusinessWatch, isPetsOwnedDisabled, hostedPreviouslyWatch, isLocalCoordinatorWatch] = watch([
    'housingType.isBusiness.value',
    'petsOwned.noPets',
    'reasons.hostedPreviously.value',
    'reasons.isLocalCoordinator.value',
  ]);

  const hostedPreviouslyDisabled = hostedPreviouslyWatch !== 'yes';
  const isLocalCoordinatorDisabled = isLocalCoordinatorWatch !== 'yes';

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <PaperSection title="Housing Type">
        <Grid2 size={4}>
          <FormField type={FormFieldType.Dropdown} label="Type of House" name="housingType.typeOfHouse" control={control} options={typeOfHouseOptions} />
        </Grid2>
        <Grid2 size={4}>
          <FormField type={FormFieldType.Dropdown} label="Area You Live In" name="housingType.areaYouLiveIn" control={control} options={areaYouLiveInOptions} />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            rows={6}
            multiline={true}
            control={control}
            name="housingType.homeDescription"
            type={FormFieldType.Text}
            label="Describe your home (must include the number of rooms, bedrooms, bathrooms, yard, the student's bedroom, etc.) and your neighborhood:"
            maxLength={MAX_CHARACTERS_DESCRIPTION}
          />
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="caption">Is the residence the site of a functioning business (e.g. daycare, farm)?</Typography>
          <Grid2 container direction="row" spacing={4}>
            <FormField control={control} options={yesNoOptions} name="housingType.isBusiness.value" type={FormFieldType.Radio} label="" />

            <FormField
              disabled={!(isBusinessWatch === 'yes')}
              control={control}
              type={FormFieldType.Checkboxset}
              name="housingType.isBusiness.type"
              options={functioningBusinessResidenceTypeOptions}
              label=""
            />
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">Please note that participants are not permitted to work or be required to work for an on site business. </Typography>
          </Alert>
        </Grid2>
      </PaperSection>
      <PaperSection title="Pets Owned">
        <Grid2 size={12}>
          <FormField type={FormFieldType.Checkbox} name="petsOwned.noPets" control={control} label="There are no pets in this household" />
        </Grid2>

        {pets.map((item, index) => (
          <Grid2 key={item.id} size={12}>
            <Paper sx={{ p: 2 }}>
              <Grid2 container spacing={3}>
                <Grid2 size={4}>
                  <FormField
                    disabled={isPetsOwnedDisabled}
                    control={control}
                    name={`petsOwned.pets.${index}.name`}
                    type={FormFieldType.Text}
                    label="Pet's Name"
                  />
                </Grid2>

                <Grid2 size={4}>
                  <FormField
                    disabled={isPetsOwnedDisabled}
                    type={FormFieldType.Dropdown}
                    label="Type of Animal"
                    name={`petsOwned.pets.${index}.type`}
                    control={control}
                    options={typeOfAnimalOptions}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <FormField
                    disabled={isPetsOwnedDisabled}
                    type={FormFieldType.Radio}
                    label="Relative Size of Animal:"
                    name={`petsOwned.pets.${index}.size`}
                    control={control}
                    options={relativeSizeOfAnimalOptions}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <FormField disabled={isPetsOwnedDisabled} type={FormFieldType.Text} label="Breed" name={`petsOwned.pets.${index}.breed`} control={control} />
                </Grid2>

                <Grid2 size={4}>
                  <FormField
                    disabled={isPetsOwnedDisabled}
                    control={control}
                    name={`petsOwned.pets.${index}.whereIsPetHoused`}
                    label="Where is the pet housed?"
                    type={FormFieldType.Checkboxset}
                    options={[
                      { label: 'Outdoors', value: 'outdoors' },
                      { label: 'Indoors', value: 'indoors' },
                    ]}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <FormField
                    disabled={isPetsOwnedDisabled}
                    id="API_PHOTO_ID"
                    type={FormFieldType.PictureUploader}
                    label="Photo (optional)"
                    name={`petsOwned.pets.${index}.photo`}
                    control={control}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Button variant="outlined" color="error" disabled={isPetsOwnedDisabled}>
                    Delete
                  </Button>
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>
        ))}

        <Grid2 size={12}>
          <Button startIcon={<AddIcon />} variant="outlined" color="primary" disabled={isPetsOwnedDisabled} onClick={() => addPet(petDefaultValues)}>
            Add another Pet
          </Button>
        </Grid2>
      </PaperSection>
      <PaperSection title="Activity Level">
        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Radio}
            label="Is your household:"
            name="activityLevel.householdActivityLevel"
            control={control}
            options={isYourHouseholdOptions}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Checkboxset}
            label="Please check the amenities that the student will have access to."
            name="activityLevel.amenitiesAccess"
            control={control}
            options={amenitiesAccessOptions}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Checkboxset}
            label="Please check the utilities that the student will have access to."
            name="activityLevel.utilitiesAccess.value"
            control={control}
            options={utilitiesAccessOptions}
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Radio}
            label="Language spoken at home:"
            name="activityLevel.languageSpokenAtHome.value"
            control={control}
            options={languageSpokenAtHomeOptions}
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Radio}
            label="Will the student have their own bedroom?"
            name="activityLevel.ownBedroom.value"
            control={control}
            options={ownBedroomOptions}
          />
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The exchange student&apos;s bedroom must contain a separate bed for the student that is neither convertible nor inflatable in nature; and the
              student must have adequate storage space for clothes and personal belongings, reasonable access to bathroom facilities, study space if not
              otherwise available in the house and reasonable, unimpeded access to the outside of the house in the event of a fire or similar emergency.
            </Typography>
          </Alert>
        </Grid2>
      </PaperSection>
      <PaperSection title="Reasons for Hosting">
        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Checkboxset}
            label="How did you learn about becoming a Greenheart Host Family?"
            name="reasons.discoverySource.value"
            control={control}
            options={discoverySourceOptions}
            itemWrapperProps={{ sx: { width: { xs: '100%', lg: '30%' } } }}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            rows={8}
            multiline
            control={control}
            label="Why does your family want to host an exchange student?"
            type={FormFieldType.Text}
            name="reasons.reason"
            maxLength={MAX_CHARACTERS_DESCRIPTION}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            rows={8}
            multiline
            control={control}
            label="What is your family hoping to learn from hosting a student from another country? What aspects of American life and culture can you share with your exchange student?"
            type={FormFieldType.Text}
            name="reasons.expectations"
            maxLength={MAX_CHARACTERS_DESCRIPTION}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            rows={8}
            multiline
            control={control}
            label="Describe any special activities planned during the school year by your family or by a school or community group that the student might elect to join that would involve extra expense for the student. Include estimated cost and dates, so that the student and natural family can discuss them prior to departure."
            type={FormFieldType.Text}
            name="reasons.specialActivities"
            maxLength={MAX_CHARACTERS_DESCRIPTION}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            rows={8}
            multiline
            control={control}
            label="What are the family's expectations of what the student's responsibilities will be?"
            type={FormFieldType.Text}
            name="reasons.studentResponsibilities"
            maxLength={MAX_CHARACTERS_DESCRIPTION}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Radio}
            label="Have you hosted for another organization?"
            name="reasons.hostedPreviously.value"
            control={control}
            options={yesNoOptions}
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            rows={8}
            multiline
            control={control}
            label="If yes, for whom and for how many years?"
            type={FormFieldType.Text}
            name="reasons.hostedPreviously.explanation"
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            disabled={hostedPreviouslyDisabled}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Radio}
            label="Will you be working as a local coordinator for another organization during the academic year for which you are applying to host?"
            name="reasons.isLocalCoordinator.value"
            control={control}
            options={yesNoOptions}
          />

          <FormField
            rows={8}
            multiline
            control={control}
            label="If yes, for whom?"
            type={FormFieldType.Text}
            name="reasons.isLocalCoordinator.explanation"
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            disabled={isLocalCoordinatorDisabled}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            type={FormFieldType.Radio}
            label="Are you working as or applying to be a local coordinator for Greenheart?"
            name="reasons.isLocalCoordinatorGH"
            control={control}
            options={yesNoOptions}
          />
        </Grid2>
      </PaperSection>
    </Stack>
  );
};

type PaperSectionProps = { title: string; children: ReactNode };

const PaperSection = ({ title, children }: PaperSectionProps) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Grid2 container spacing={3}>
        <Grid2 size={12}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        </Grid2>
        {children}
      </Grid2>
    </Paper>
  );
};
